import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { TopoLines } from './TopoLines';
import { PAGE_IMAGES } from '../data/assets';
type Page = {
  src: string;
  alt: string;
  caption: string;
};

/**
 * "Peek inside" — páginas REALES del producto (Volumen 2 en Gamma).
 * Si alguna no carga, se oculta y queda el motivo topográfico de fondo.
 */
const pages: Page[] = [{
  src: PAGE_IMAGES.formula,
  alt: 'The Grab-and-Lead Meeting Formula as 45, 60 and 90-minute timeline bars',
  caption: 'The 5-block formula in 45, 60 and 90 minutes'
}, {
  src: PAGE_IMAGES.meetingCard,
  alt: 'A Meeting Card with an overhead hall diagram, timeline and materials',
  caption: 'Page A · Meeting Card: setup, timing, what to say'
}, {
  src: PAGE_IMAGES.skillStation,
  alt: 'A Skill Station page teaching whipping, coiling and the square knot',
  caption: 'Page B · Skill Station, explained for the adult first'
}, {
  src: PAGE_IMAGES.patrolChallenge,
  alt: 'A Patrol Challenge and Game page with scoring, Age Dial and Rain Swap',
  caption: 'Page C · Challenge & game, with Age Dial and Rain Swap'
}, {
  src: PAGE_IMAGES.wrongRight,
  alt: 'A wrong vs right comparison of seven knots with the fix for each',
  caption: 'Wrong vs. right: the fix for every common mistake'
}, {
  src: PAGE_IMAGES.worksheet,
  alt: 'A printable Knot Passport worksheet with initials-only fields',
  caption: 'Printable worksheets kids actually use'
}];
export function LookInside() {
  const trackRef = useRef<HTMLUListElement>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);
  const updateArrows = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);
  useEffect(() => {
    updateArrows();
    window.addEventListener('resize', updateArrows);
    return () => window.removeEventListener('resize', updateArrows);
  }, [updateArrows]);
  const scrollByCard = (direction: 1 | -1) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector('li');
    const step = card ? card.getBoundingClientRect().width + 16 : el.clientWidth * 0.8;
    el.scrollBy({
      left: step * direction,
      behavior: 'smooth'
    });
  };
  const arrowClass = 'absolute top-[42%] z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-paper-dark bg-white text-pine shadow-card transition-[opacity,background-color,transform] duration-150 ease-out hover:bg-paper active:scale-95 focus:outline-none focus-visible:ring-4 focus-visible:ring-ember/50 disabled:pointer-events-none disabled:opacity-0';
  return <section aria-labelledby="peek-heading" className="topo-light w-full bg-paper" data-id="element-141" data-mp="255b2072uyrs2">
      <div className="mx-auto max-w-6xl px-5 pt-14 sm:pt-20" data-id="element-142" data-mp="2a3w5qghbjtl9">
        <div className="text-center" data-id="element-143" data-mp="2k8kvqtzje1wk">
          <p className="eyebrow text-ember-deep" data-id="element-144" data-mp="2k7j7fntifiww">A peek inside</p>
          <h2 id="peek-heading" className="trail-underline mt-3 font-display text-[30px] leading-tight text-pine sm:text-5xl" data-id="element-145" data-mp="2fa1a6uouus4q">
            Real pages from the pack
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-pine-soft" data-id="element-146" data-mp="26i92usv4nz4d">
            Not blank templates. Every meeting is drawn, diagrammed and ready
            to print — here’s a look at Volume 2, Knots, Ropes &amp; Pioneering.
          </p>
        </div>

        <div className="relative mt-8" data-id="element-147" data-mp="1wlmgu0r2f20r">
          <TopoLines className="pointer-events-none absolute -left-10 -top-6 hidden h-40 w-40 text-bark/10 lg:block" data-id="element-148" data-mp="1rjf253eup7uq" />
          <button type="button" onClick={() => scrollByCard(-1)} disabled={!canPrev} aria-label="Previous page" className={`${arrowClass} left-0 sm:-left-2`} data-id="element-149" data-mp="1njwpeux6o32z">
            <ChevronLeftIcon className="h-6 w-6" aria-hidden="true" data-id="element-150" data-mp="3huib4iimdr4a" />
          </button>
          <button type="button" onClick={() => scrollByCard(1)} disabled={!canNext} aria-label="Next page" className={`${arrowClass} right-0 sm:-right-2`} data-id="element-151" data-mp="3p0m0af3inbps">
            <ChevronRightIcon className="h-6 w-6" aria-hidden="true" data-id="element-152" data-mp="3k0pxgj6k1qbt" />
          </button>
          <ul ref={trackRef} onScroll={updateArrows} className="-mx-5 flex snap-x snap-mandatory gap-4 overflow-x-auto px-5 pb-5 [scrollbar-width:thin]" data-id="element-153" data-mp="3k2kli6d4hyk4">
            {pages.map(page => <li key={page.src} className="shrink-0 basis-[68%] snap-start sm:basis-[232px]" data-id="element-154" data-mp="2wbni5hze89kh">
                <figure className="overflow-hidden rounded-2xl border border-paper-dark bg-white shadow-card" data-id="element-155" data-mp="31aru2gdpclei">
                  <div className="relative flex aspect-[9/16] w-full items-center justify-center overflow-hidden bg-paper-dark" data-id="element-156" data-mp="2ncjyocwbds5g">
                    <TopoLines className="pointer-events-none absolute h-24 w-24 text-bark/20" data-id="element-157" data-mp="28ni9tctz62bz" />
                    <img src={page.src} alt={page.alt} loading="lazy" onError={e => {
                  e.currentTarget.style.display = 'none';
                }} className="absolute inset-0 h-full w-full object-cover object-top" data-id="element-158" data-mp="23ox8fa2zlcly" />
                  </div>
                  <figcaption className="px-3.5 py-3 text-[13px] font-semibold leading-snug text-pine" data-id="element-159" data-mp="2mnt28kaxnoz9">
                    {page.caption}
                  </figcaption>
                </figure>
              </li>)}
          </ul>
          <p className="field-note mt-1 text-center text-xl text-pine-soft sm:text-2xl" data-id="element-160" data-mp="2wenovps3cql8">
            60 meetings · 3 pages each · 24 worksheets across the six volumes
          </p>
        </div>
      </div>
    </section>;
}